// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useContext } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  useScheduledEventListPage,
  Sort,
  unsetEventTypeFilter,
  EventStatus,
} from './hooks';

import {
  black,
  blueGrayPale,
  gray01,
  gray03,
} from 'presentations/views/styles/color';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import ScheduledEventList from 'presentations/views/components/organisms/ScheduledEventList';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  fontSize,
  hideScrollbar,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Pagination from 'presentations/views/components/atoms/Pagination';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import Select, { Option } from 'presentations/views/components/atoms/Select';
import DashboardHomeNavigation from 'presentations/views/components/organisms/DashboardHomeNavigation';
import DashboardProjectNavigation from 'presentations/views/components/organisms/DashboardProjectNavigation';
import { scheduledEventListPath } from 'presentations/routes/path';
import HostPendingEventList from 'presentations/views/components/organisms/HostPendingEventList';
import { PendingEventType } from 'domains/models/pending_event/pendingEventType';
import ToggleLinkButton from 'presentations/views/components/atoms/ToggleLinkButton';
import Image from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import TextFieldDateRangePicker from 'presentations/views/components/atoms/TextFieldDateRangePicker';
import PlanUpgradeDialog from 'presentations/views/components/organisms/PlanUpgradeDialog';
import Tooltip from 'presentations/views/components/atoms/Tooltip';

interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;

  const { state, actions } = useScheduledEventListPage({
    viewer: viewer,
  });

  let userId: string | null = state.currentUserId || viewer.uid;
  if (userId === viewer.uid) userId = null;
  const projectId = state.projectUid;

  const eventTypeFilterOptions: Option[] = [
    {
      value: unsetEventTypeFilter,
      text: t('EventType'),
    },
  ];
  if (state.enabledEventTypes.length > 0) {
    eventTypeFilterOptions.push({
      group: state.enabledEventTypes.map((eventType) => ({
        text: eventType.name,
        value: eventType.id,
      })),
      groupName: t('Enabled'),
      value: 'enabled',
    });
  }
  if (state.disabledEventTypes.length > 0) {
    eventTypeFilterOptions.push({
      group: state.disabledEventTypes.map((eventType) => ({
        text: eventType.name,
        value: eventType.id,
      })),
      groupName: t('Disabled'),
      value: 'disabled',
    });
  }

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      tabBarContent={
        <Container>
          {state.projectUid ? (
            <DashboardProjectNavigation
              activeIndex={1}
              viewer={viewer}
              projectId={state.projectUid}
              onChangeFilter={actions.onChangeFilter}
            />
          ) : (
            <DashboardHomeNavigation
              activeIndex={1}
              userId={state.currentUserId || viewer.uid}
              viewer={viewer}
              onChangeFilter={actions.onChangeFilter}
            />
          )}
          <ContentHeader>
            <ToggleGroupButtonContainer>
              <ToggleGroupButtonScrollArea>
                <ToggleButtonContainer>
                  <ToggleLinkButton
                    index={0}
                    // onClick={actions.onChangeTab}
                    link={scheduledEventListPath({
                      userId,
                      projectId,
                      filters: state.query,
                    })}
                    isActive={state.activeTab === 0}
                  >
                    {t('Incoming')}
                    {state.incomingCount > 0 && (
                      <TodoCount>{state.incomingCount}</TodoCount>
                    )}
                  </ToggleLinkButton>
                </ToggleButtonContainer>
                <ToggleButtonContainer>
                  <ToggleLinkButton
                    index={1}
                    link={scheduledEventListPath({
                      userId,
                      projectId,
                      tab: 1,
                      filters: state.query,
                    })}
                    isActive={state.activeTab === 1}
                  >
                    {t('Done')}
                  </ToggleLinkButton>
                </ToggleButtonContainer>
                {userId !== 'all' && !state.projectUid && (
                  <ToggleButtonContainer>
                    <ToggleLinkButton
                      index={2}
                      link={scheduledEventListPath({ userId, tab: 2 })}
                      isActive={state.activeTab === 2}
                    >
                      {t('Pending')}
                      {state.pendingCount > 0 && (
                        <TodoCount>{state.pendingCount}</TodoCount>
                      )}
                    </ToggleLinkButton>
                  </ToggleButtonContainer>
                )}
                {!state.currentUserId && !state.projectUid && (
                  <ToggleButtonContainer>
                    <ToggleLinkButton
                      index={3}
                      link={scheduledEventListPath({
                        userId,
                        projectId: null,
                        tab: 3,
                        filters: state.query,
                      })}
                      isActive={state.activeTab === 3}
                    >
                      {t('Booking as guest')}
                    </ToggleLinkButton>
                  </ToggleButtonContainer>
                )}
              </ToggleGroupButtonScrollArea>
            </ToggleGroupButtonContainer>
            {state.activeTab !== 2 && (
              <FilterAndSortContainer>
                <FilterContainer
                  isSelectedDateRange={
                    !!state.startedAtFilter && !!state.endedAtFilter
                  }
                  isGuestEvents={state.activeTab === 3}
                >
                  {state.activeTab === 3 ? (
                    <>
                      <StatusContainer>
                        <StatusSelectContainer
                          isGuestEvents={state.activeTab === 3}
                        >
                          <StatusSelect
                            value={state.eventStatusFilter}
                            onChange={actions.onChangeEventStatusFilter}
                            size="small"
                            options={[
                              {
                                value: EventStatus.all,
                                text: t('Status all'),
                              },
                              {
                                value: EventStatus.active,
                                text: t('Status active'),
                              },
                              {
                                value: EventStatus.cancelled,
                                text: t('Status cancelled'),
                              },
                            ]}
                            isContainerFullWidth
                          />
                        </StatusSelectContainer>
                      </StatusContainer>

                      <DateRangeContainer
                        isSelectedDateRange={
                          !!state.startedAtFilter && !!state.endedAtFilter
                        }
                        isGuestEvents={state.activeTab === 3}
                      >
                        <TextFieldDateRangePicker
                          startedAt={state.startedAtFilter ?? undefined}
                          endedAt={state.endedAtFilter ?? undefined}
                          onChange={actions.onChangeDateFilter}
                          locale={state.locale}
                          timeZone={state.timeZone}
                          size="medium"
                          placeholder={t('DateTime')}
                          textFieldHeight={40}
                          initializedDateRange={state.isInitialized}
                        />
                      </DateRangeContainer>
                    </>
                  ) : (
                    <>
                      <EventTypeAndStatusContainer>
                        {state.activeTab !== 3 && (
                          <EventTypeSelect
                            value={state.eventTypeFilter}
                            onChange={actions.onChangeEventTypeFilter}
                            size="small"
                            options={eventTypeFilterOptions}
                            isContainerFullWidth
                          />
                        )}
                        <StatusSelectContainer
                          isGuestEvents={state.activeTab === 3}
                        >
                          <StatusSelect
                            value={state.eventStatusFilter}
                            onChange={actions.onChangeEventStatusFilter}
                            size="small"
                            options={[
                              {
                                value: EventStatus.all,
                                text: t('Status all'),
                              },
                              {
                                value: EventStatus.active,
                                text: t('Status active'),
                              },
                              {
                                value: EventStatus.cancelled,
                                text: t('Status cancelled'),
                              },
                            ]}
                            isContainerFullWidth
                          />
                        </StatusSelectContainer>
                      </EventTypeAndStatusContainer>

                      <DateRangeContainer
                        isSelectedDateRange={
                          !!state.startedAtFilter && !!state.endedAtFilter
                        }
                      >
                        <TextFieldDateRangePicker
                          startedAt={state.startedAtFilter ?? undefined}
                          endedAt={state.endedAtFilter ?? undefined}
                          onChange={actions.onChangeDateFilter}
                          locale={state.locale}
                          timeZone={state.timeZone}
                          size="medium"
                          placeholder={t('DateTime')}
                          textFieldHeight={40}
                          initializedDateRange={state.isInitialized}
                        />
                      </DateRangeContainer>
                    </>
                  )}
                </FilterContainer>
                <SortAndOrderContainer>
                  <SortSelect
                    isSkeleton={true}
                    name="sort"
                    size="small"
                    variant="standard"
                    value={state.sort}
                    onChange={actions.onChangeSort}
                    options={[
                      {
                        value: Sort.created,
                        text: t('Sort by Created'),
                      },
                      {
                        value: Sort.startDate,
                        text: t('Sort by Start Date'),
                      },
                    ]}
                  />
                  <OrderContainer onClick={actions.onChangeOrder}>
                    <Image
                      src={`pages/dashboard/order_${
                        state.order === 'asc' ? 'ascending' : 'descending'
                      }.svg`}
                      alt=""
                      width={24}
                      height={24}
                    />
                  </OrderContainer>
                  {state.activeTab !== 3 && (
                    <CsvExportContainer>
                      <Tooltip title={t('CSV')}>
                        <CsvExportLink
                          href={state.csvExportUrl}
                          download={
                            state.csvExportUrl ? 'events.csv' : undefined
                          }
                          onClick={actions.onClickCsvExport}
                        >
                          <CsvExportIcon
                            name="file-download-outline"
                            size="24px"
                            color="inherit"
                          />
                        </CsvExportLink>
                      </Tooltip>
                    </CsvExportContainer>
                  )}
                </SortAndOrderContainer>
              </FilterAndSortContainer>
            )}
          </ContentHeader>
          <Content>
            {state.activeTab === 0 && (
              <ScheduledEventList
                isLoading={state.isLoading}
                events={state.events}
                viewer={viewer}
                onCancelEvent={actions.onCancelEvent}
                isGuest={false}
              />
            )}
            {state.activeTab === 1 && (
              <ScheduledEventList
                isLoading={state.isLoading}
                events={state.events}
                viewer={viewer}
                onCancelEvent={actions.onCancelEvent}
                isGuest={false}
              />
            )}
            {state.activeTab === 2 && (
              <HostPendingEventList
                isLoading={state.isLoadingEventType}
                viewer={viewer}
                pendingEvents={state.pendingEvents || []}
                onSelectMenu={actions.onSelectMenu}
                canAddEventType={false}
                type="one-off"
              />
            )}
            {state.activeTab === 3 && (
              <ScheduledEventList
                isLoading={state.isLoading}
                events={state.events}
                viewer={viewer}
                onCancelEvent={actions.onCancelEvent}
                isGuest={true}
              />
            )}
            {state.numberOfPages > 1 && (
              <PaginationContainer>
                <Pagination
                  currentPage={state.currentPage}
                  numberOfPages={state.numberOfPages}
                  onChange={actions.onChangePage}
                />
              </PaginationContainer>
            )}
          </Content>
          <ConfirmationDialog
            isLoading={state.isConfirmLoading}
            isOpen={state.deletePendingEvent !== null}
            onSubmit={actions.onDeleteEvent}
            onCancel={actions.onCloseDialog}
            title={
              state.deletePendingEvent?.pendingEventType ===
              PendingEventType.Poll
                ? t('Delete poll?')
                : t('Delete custom?')
            }
            submitLabel={t('Delete')}
          />
          {state.isShowingPlanUpgrade && (
            <PlanUpgradeDialog onClose={actions.onClosePlanUpgrade} />
          )}
        </Container>
      }
    />
  );
}

const Container = styled.div`
  background: ${blueGrayPale};
`;

const Content = styled.div`
  background: ${blueGrayPale};
  padding: ${spacing(24)} ${spacing(24)} 0;
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(12)} ${spacing(12)} 0;
  }
`;
const ContentHeader = styled.div`
  width: 100%;
  padding: ${spacing(24)} ${spacing(24)} 0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${mediaMax.desktop} {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }
  ${mediaMax.tablet} {
    padding: ${spacing(16)} ${spacing(12)} 0;
  }
`;
const PaginationContainer = styled.div`
  padding: ${spacing(48)} 0;
  display: flex;
  justify-content: center;
`;

const FilterAndSortContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const FilterContainer = styled.div<{
  isSelectedDateRange?: boolean;
  isGuestEvents?: boolean;
}>`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    width: 100%;
    justify-content: space-between;
    margin-bottom: ${spacing(8)};

    ${({ isSelectedDateRange }) =>
      isSelectedDateRange &&
      css`
        flex-direction: column;
        align-items: flex-start;
      `}
  }
  ${mediaMax.smartphone} {
    align-items: flex-start;
    ${({ isGuestEvents }) =>
      isGuestEvents
        ? css`
            flex-direction: row;
          `
        : css`
            flex-direction: column;
          `}
  }
`;
const EventTypeAndStatusContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    width: 100%;
    justify-content: space-between;
  }
`;
const EventTypeSelect = styled(Select)`
  width: 173px;
  & .MuiInputBase-root {
    font-size: ${fontSize(14)};
  }
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    width: 100%;
    justify-content: space-between;
  }
`;
const StatusSelectContainer = styled.div<{ isGuestEvents: boolean }>`
  ${({ isGuestEvents }) =>
    !isGuestEvents &&
    css`
      margin-left: ${spacing(8)};
    `};
  width: 132px;
  flex-shrink: 0;
`;
const StatusSelect = styled(Select)`
  width: 132px;
  & .MuiInputBase-root {
    font-size: ${fontSize(14)};
  }
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const DateRangeContainer = styled.div<{
  isSelectedDateRange?: boolean;
  isGuestEvents?: boolean;
}>`
  margin-left: ${spacing(8)};

  ${mediaMax.tablet} {
    ${({ isSelectedDateRange, isGuestEvents }) =>
      isSelectedDateRange &&
      css`
        margin-top: ${!isGuestEvents ? spacing(8) : 0};
        margin-left: ${isGuestEvents ? spacing(8) : 0};
      `}
  }
  ${mediaMax.smartphone} {
    ${({ isGuestEvents }) =>
      !isGuestEvents &&
      css`
        margin-top: ${spacing(8)};
      `}

    ${({ isGuestEvents }) =>
      isGuestEvents
        ? css`
            margin-left: ${spacing(8)};
          `
        : css`
            margin-left: 0;
          `}
  }
`;
const SortAndOrderContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 175px;
`;
const SortSelect = styled(Select)`
  border: 0;
  background-color: initial;
  margin-left: ${spacing(8)};

  select {
    color: ${black};
    font-size: ${fontSize(14)};
  }
  .MuiInputBase-root {
    padding-left: ${spacing(8)};
  }
  .MuiInput-underline:before {
    border: 0 !important;
  }
  .MuiInput-underline:after {
    border: 0 !important;
  }
  .MuiSelect-select:focus {
    background-color: transparent !important;
  }
`;
const OrderContainer = styled.div`
  cursor: pointer;
  margin-left: ${spacing(8)};
`;
const CsvExportContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${spacing(16)};
  padding-left: ${spacing(16)};
  border-left: 1px solid ${gray03};
`;
const CsvExportLink = styled.a`
  height: 24px;
`;
const CsvExportIcon = styled(Icon)`
  cursor: pointer;
  fill: ${gray01};
`;

const ToggleGroupButtonContainer = styled.div`
  display: flex;
  width: 100%;
  ${mediaMax.desktop} {
    margin-bottom: ${spacing(8)};
  }
`;

const ToggleGroupButtonScrollArea = styled.div`
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;
  ${hideScrollbar};
`;

const ToggleButtonContainer = styled.span`
  flex: none;
  min-width: 150px;
  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
  }
`;

const TodoCount = styled.span`
  font-size: ${fontSize(12)};
  margin-left: ${spacing(8)};
`;
